// Tour step properties:
//   - aboveDrawer: (bool) step targets an element inside a drawer overlay — adjusts z-index accordingly
//   - awaitClick: (string) CSS selector — the tour pauses until the user clicks this element
//   - awaitClickAdvanceDelay: (number) ms to wait after the click before moving to the next step
//   - awaitClickAll: (bool) listen for click on ALL elements matching awaitClick, not just the first
//   - awaitClickDelegate: (bool) use event delegation for awaitClick — resilient to DOM re-renders (e.g. FullCalendar)
//   - awaitClickLast: (bool) target the last matching element and block clicks on all others
//   - blockClickOnlySelectors: (string[]) CSS selectors whose click events are blocked (mousedown allowed for drag-and-drop)
//   - blockClickSelectors: (string[]) CSS selectors whose click AND mousedown events are blocked
//   - blockTarget: (bool|string) true = block clicks on the spotlight target; string = block clicks on that selector
//   - blockTargetException: (string|string[]) inside a blocked target, these selectors stay clickable
//   - blockTargetSiblings: (bool) block all siblings inside the target container, only the awaitClick element stays clickable
//   - congrats: (bool) marks the final congratulatory step (triggers confetti)
//   - content: (string) i18n key for the step body text
//   - hideFinish: (bool) hide the Finish button (used when chaining to another tour)
//   - hidePrev: (bool) hide the Previous button for this step
//   - highlightDays: (bool) add a pulsing highlight to calendar day cells
//   - nextTour: (string) id of the tour to start when this step or tour ends (chaining)
//   - noSpotlight: (bool) disable the spotlight cutout (full overlay, e.g. for congrats)
//   - position: (string) tooltip placement relative to the target — top | bottom | left | right | center
//   - pulse: (bool) show a pulsing animation on the awaitClick element (default: true when awaitClick is set)
//   - raiseAncestors: (bool) raise ancestor stacking contexts so the target escapes its parent z-index
//   - scrollTo: (string) 'top' scrolls the page to top instead of centering the target
//   - spotlightPadding: (number) extra px around the target in the spotlight cutout
//   - target: (string) CSS selector of the element to highlight
//   - title: (string) i18n key for the step title
//   - watchTargets: (string[]) extra CSS selectors included in the spotlight area

export default {
  id: 'bots',
  route: 'bots',
  steps: [
    // --- Step 1: Main bots list overview ---
    {
      target: '.section-bots .bots-main',
      title: 'tours.bots.steps.overview.title',
      content: 'tours.bots.steps.overview.content',
      position: 'center',
      spotlightPadding: 10,
      blockTarget: true,
      blockOutsideSpotlight: true,
    },
    // --- Step 2: Add bot button ---
    {
      target: '.bots-main [data-qa="add-bot"]',
      title: 'tours.bots.steps.addBot.title',
      content: 'tours.bots.steps.addBot.content',
      position: 'bottom',
      spotlightPadding: 8,
      blockTarget: true,
      blockOutsideSpotlight: true
    },
    // --- Step 3: Bots list table ---
    {
      target: '.vs-table--thead',
      title: 'tours.bots.steps.botsList.title',
      content: 'tours.bots.steps.botsList.content',
      position: 'bottom',
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 4: Single bot row ---
    {
      target: '.bots-table tr.row',
      title: 'tours.bots.steps.botRow.title',
      content: 'tours.bots.steps.botRow.content',
      position: 'top',
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 5: Edit bot button (click to open form) ---
    {
      target: '.bots-table .column.actions .btn-large',
      title: 'tours.bots.steps.editBot.title',
      content: 'tours.bots.steps.editBot.content',
      position: 'left',
      awaitClick: '.bots-table .column.actions .btn-large',
      awaitClickAdvanceDelay: 800,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 6: Bot edit form overview ---
    {
      target: '.bots-new .bots-wrapper',
      title: 'tours.bots.steps.editForm.title',
      content: 'tours.bots.steps.editForm.content',
      position: 'center',
      spotlightPadding: { top: -40, bottom: 0, left: 40, right: 40 },
      scrollTo: 'top',
      hidePrev: true,
      raiseAncestors: true,
      blockTarget: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 7: Bot name ---
    {
      target: '.bots-new .title.box',
      title: 'tours.bots.steps.botName.title',
      content: 'tours.bots.steps.botName.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 8: Source type selection ---
    {
      target: '.bots-new .bot-type .display-wrapper',
      title: 'tours.bots.steps.sourceType.title',
      content: 'tours.bots.steps.sourceType.content',
      position: 'right',
      spotlightPadding: 6,
      blockTarget: true,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 9: Prefix & suffix hashtags ---
    {
      target: '.hashtags.prefix.box',
      title: 'tours.bots.steps.prefixSuffix.title',
      content: 'tours.bots.steps.prefixSuffix.content',
      position: 'left',
      spotlightPadding: 6,
      watchTargets: ['.hashtags.suffix.box'],
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 10: Mentions & hashtags column ---
    {
      target: '.bots-new .column .right',
      title: 'tours.bots.steps.mentionsHashtags.title',
      content: 'tours.bots.steps.mentionsHashtags.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 11: Post type selection ---
    {
      target: '.post-type',
      title: 'tours.bots.steps.postType.title',
      content: 'tours.bots.steps.postType.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 12: Content type ---
    {
      target: '.bots-wrapper > .post-content:not(.bot-type) .display-wrapper',
      title: 'tours.bots.steps.contentType.title',
      content: 'tours.bots.steps.contentType.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 13: Link position ---
    {
      target: '.bots-wrapper > .post-link:not(.bot-type)',
      title: 'tours.bots.steps.linkPosition.title',
      content: 'tours.bots.steps.linkPosition.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },

    // --- Step 14: Posting frequency ---
    {
      target: '.bots-new .frequency',
      title: 'tours.bots.steps.frequency.title',
      content: 'tours.bots.steps.frequency.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 15: Daily post limit ---
    {
      target: '.bots-new .bots-wrapper > .limit',
      title: 'tours.bots.steps.dailyLimit.title',
      content: 'tours.bots.steps.dailyLimit.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 16: Planning schedule ---
    {
      target: '.planning',
      title: 'tours.bots.steps.planning.title',
      content: 'tours.bots.steps.planning.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 17: After auto-post actions ---
    {
      target: '.after-auto-post',
      title: 'tours.bots.steps.afterAutoPost.title',
      content: 'tours.bots.steps.afterAutoPost.content',
      position: 'left',
      spotlightPadding: 6,
      raiseAncestors: true,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Step 18: Save / Cancel buttons ---
    {
      target: '.buttons-wrapper',
      title: 'tours.bots.steps.buttonsWrapper.title',
      content: 'tours.bots.steps.buttonsWrapper.content',
      position: 'top',
      spotlightPadding: 6,
      scrollToBottom: true,
      raiseAncestors: true,
      awaitClick: '.buttons-wrapper .btn-large',
      awaitClickAll: true,
      awaitClickAdvanceDelay: 800,
      skipIfMissing: true,
      blockOutsideSpotlight: true
    },
    // --- Congrats (back on main view, chains to pages tour) ---
    {
      title: 'tours.bots.steps.congrats.title',
      content: 'tours.bots.steps.congrats.content',
      position: 'center',
      noSpotlight: true,
      congrats: true,
      nextTour: 'pages',
      blockOutsideSpotlight: true
    }
  ]
}
